import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Phone, Mail, Building2, IndianRupee, User, Clock, Filter, AlertTriangle, CheckCircle, RefreshCw, Edit, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Calendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // month, week, day
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [userRole, setUserRole] = useState(null);
  
  // Business context for filtering
  const { selectedBusiness, businessId } = useBusiness();
  
  // Meeting action dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionType, setActionType] = useState("complete"); // complete or reschedule
  const [actionRemarks, setActionRemarks] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  
  // Update Follow-up dialog state
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateEvent, setUpdateEvent] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: "pending", next_datetime: "", remarks: "" });
  const [updateSaving, setUpdateSaving] = useState(false);

  // Count missed events
  const missedCount = useMemo(() => {
    return events.filter(e => e.is_missed).length;
  }, [events]);

  useEffect(() => {
    fetchUserRole();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [startDateFilter, endDateFilter, businessId, userRole]);
  
  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      let url = `${BACKEND_URL}/api/calendar/events`;
      const params = new URLSearchParams();
      if (startDateFilter) params.append("start_date", startDateFilter);
      if (endDateFilter) params.append("end_date", endDateFilter);
      // Add business filter for admin and super_admin users
      if (businessId && (userRole === "admin" || userRole === "super_admin")) {
        params.append("business_id", businessId);
      }
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error("Error fetching calendar events:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle meeting action (complete or reschedule)
  const handleMeetingAction = async () => {
    if (!selectedEvent) return;
    
    if (actionType === "reschedule" && !rescheduleDate) {
      toast.error("Please select a new date for rescheduling");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${selectedEvent.lead_id}/meeting-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: actionType,
          remarks: actionRemarks || null,
          new_date: actionType === "reschedule" ? rescheduleDate : null
        }),
      });
      
      if (response.ok) {
        toast.success(actionType === "complete" ? "Meeting marked as completed!" : "Meeting rescheduled successfully!");
        setActionDialogOpen(false);
        setSelectedEvent(null);
        setActionRemarks("");
        setRescheduleDate("");
        fetchEvents(); // Refresh events
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update meeting");
      }
    } catch (error) {
      toast.error("Error updating meeting");
    }
  };
  
  // Open action dialog for a specific event
  const openActionDialog = (event, action) => {
    setSelectedEvent(event);
    setActionType(action);
    setActionRemarks("");
    setRescheduleDate("");
    setActionDialogOpen(true);
  };

  // Open Update Follow-up dialog
  const openUpdateDialog = (event) => {
    setUpdateEvent(event);
    setUpdateForm({
      status: event.meeting_completed ? "done" : event.is_missed ? "missed" : "pending",
      next_datetime: event.start ? event.start.substring(0, 16) : "",
      remarks: event.notes || event.description || "",
    });
    setUpdateDialogOpen(true);
  };

  // Handle Update Follow-up save
  const handleUpdateFollowUp = async () => {
    if (!updateEvent || !updateEvent.lead_id) return;
    setUpdateSaving(true);
    try {
      const leadId = updateEvent.lead_id;
      const payload = {};

      if (updateForm.status === "done") {
        // Mark as completed
        payload.action = "complete";
        payload.remarks = updateForm.remarks;
      } else if (updateForm.status === "not_interested") {
        payload.action = "complete";
        payload.remarks = updateForm.remarks || "Not Interested";
        payload.stage = "lost";
      } else if (updateForm.status === "converted") {
        payload.action = "complete";
        payload.remarks = updateForm.remarks || "Converted";
        payload.stage = "won";
      } else {
        // Pending / Reschedule
        payload.action = "reschedule";
        payload.new_date = updateForm.next_datetime;
        payload.remarks = updateForm.remarks;
      }

      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/meeting-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // If status change requested, also update lead stage
        if (updateForm.status === "not_interested" || updateForm.status === "converted") {
          const stage = updateForm.status === "converted" ? "Won" : "Lost";
          await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ stage, remark: updateForm.remarks }),
          });
        }

        // If new follow-up date, update the lead
        if (updateForm.status === "pending" && updateForm.next_datetime) {
          await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              next_meeting: updateForm.next_datetime,
              meeting_notes: updateForm.remarks,
              meeting_completed: false,
            }),
          });
        }

        // Add remark if provided
        if (updateForm.remarks && updateForm.remarks.trim()) {
          await fetch(`${BACKEND_URL}/api/leads/${leadId}/remarks`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ text: updateForm.remarks }),
          }).catch(() => {});
        }

        toast.success("Follow-up updated successfully!");
        setUpdateDialogOpen(false);
        setUpdateEvent(null);
        fetchEvents();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update follow-up");
      }
    } catch (e) {
      toast.error("Error updating follow-up");
    } finally {
      setUpdateSaving(false);
    }
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: "Pending", className: "bg-amber-100 text-amber-700 border-amber-200" },
      done: { label: "Done", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      not_interested: { label: "Not Interested", className: "bg-red-100 text-red-700 border-red-200" },
      converted: { label: "Converted", className: "bg-blue-100 text-blue-700 border-blue-200" },
      missed: { label: "Missed", className: "bg-red-500 text-white" },
    };
    const c = configs[status] || configs.pending;
    return <Badge className={`text-xs ${c.className}`}>{c.label}</Badge>;
  };

  // Generate calendar days for month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentDate]);

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return events.filter((event) => {
      const eventDate = parseISO(event.start);
      return isSameDay(eventDate, day);
    });
  };

  // Get selected day events
  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  const eventTypeColors = {
    meeting: "bg-amber-100 text-amber-700 border-amber-200",
    follow_up: "bg-teal-100 text-teal-700 border-teal-200",
    call: "bg-green-100 text-green-700 border-green-200",
    missed: "bg-red-100 text-red-700 border-red-200",
  };

  const eventDotColors = {
    meeting: "bg-amber-500",
    follow_up: "bg-teal-500",
    call: "bg-green-500",
    missed: "bg-red-500",
  };

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const clearFilters = () => {
    setStartDateFilter("");
    setEndDateFilter("");
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">
            Follow Up
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            View all meetings and follow-ups
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Missed Events Alert */}
          {missedCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700" data-testid="missed-count-badge">
                {missedCount} Missed
              </span>
            </div>
          )}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[120px] bg-white border border-slate-200 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="day">Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date Filter */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filter:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                data-testid="calendar-start-date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                className="w-[150px] bg-slate-50 border border-slate-200 rounded-lg text-sm"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                data-testid="calendar-end-date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                className="w-[150px] bg-slate-50 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            {(startDateFilter || endDateFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700 text-xs"
              >
                Clear
              </Button>
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${eventDotColors.meeting}`}></span> Meeting
              </span>
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${eventDotColors.call}`}></span> Call
              </span>
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${eventDotColors.follow_up}`}></span> Follow-up
              </span>
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${eventDotColors.missed}`}></span> Missed
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-600" />
                {format(currentDate, "MMMM yyyy")}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToToday}
                  className="text-xs rounded-lg"
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPreviousMonth}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNextMonth}
                  className="h-8 w-8"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[400px] flex items-center justify-center text-slate-500">
                Loading calendar...
              </div>
            ) : (
              <>
                {/* Days Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-semibold text-slate-500 py-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => {
                    const dayEvents = getEventsForDay(day);
                    const hasMissedEvent = dayEvents.some(e => e.is_missed);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());
                    const isSelected = selectedDate && isSameDay(day, selectedDate);

                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedDate(day)}
                        data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                        className={`
                          min-h-[80px] p-1 rounded-lg border transition-all text-left relative
                          ${isCurrentMonth ? "bg-white dark:bg-slate-800" : "bg-slate-50 dark:bg-slate-900/50"}
                          ${hasMissedEvent ? "border-red-400 ring-1 ring-red-300 bg-red-50/30" : (isToday ? "border-indigo-500 ring-1 ring-indigo-500" : "border-slate-100 dark:border-slate-700")}
                          ${isSelected ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400" : ""}
                          hover:border-indigo-300 hover:bg-indigo-50/50
                        `}
                      >
                        {/* Red flag indicator for missed events */}
                        {hasMissedEvent && (
                          <div className="absolute top-1 right-1">
                            <AlertTriangle className="w-3 h-3 text-red-500" />
                          </div>
                        )}
                        <div className={`text-sm font-medium mb-1 ${
                          hasMissedEvent ? "text-red-600" :
                          isToday ? "text-indigo-600" : 
                          isCurrentMonth ? "text-slate-700 dark:text-slate-300" : "text-slate-400"
                        }`}>
                          {format(day, "d")}
                        </div>
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 2).map((event) => (
                            <div
                              key={event.event_id}
                              className={`text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-1 ${
                                event.is_missed ? eventTypeColors.missed : (eventTypeColors[event.type] || "bg-slate-100 text-slate-700")
                              }`}
                            >
                              {event.is_missed && <AlertTriangle className="w-2 h-2 shrink-0" />}
                              <span className="truncate">{event.lead_name}</span>
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-[10px] text-slate-500 px-1">
                              +{dayEvents.length - 2} more
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Selected Day Events */}
        <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-bold text-slate-900 dark:text-white">
              {selectedDate ? format(selectedDate, "EEEE, MMMM d") : "Select a day"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Click on a day to view events
              </p>
            ) : selectedDayEvents.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                No events on this day
              </p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {selectedDayEvents.map((event) => (
                  <div
                    key={event.event_id}
                    data-testid={`event-${event.event_id}`}
                    className={`p-4 rounded-xl border ${
                      event.is_missed ? "bg-red-50 border-red-200" : (eventTypeColors[event.type] || "bg-slate-50 border-slate-200")
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {event.is_missed && (
                          <Badge className="text-xs bg-red-500 text-white" data-testid={`missed-badge-${event.event_id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Missed
                          </Badge>
                        )}
                        <Badge className={`text-xs ${
                          event.type === "meeting" ? "bg-amber-500 text-white" :
                          event.type === "call" ? "bg-green-500 text-white" :
                          "bg-teal-500 text-white"
                        }`}>
                          {event.type.replace("_", " ")}
                        </Badge>
                        {event.meeting_completed && (
                          <Badge className="text-xs bg-emerald-500 text-white">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(parseISO(event.start), "h:mm a")}
                      </span>
                    </div>
                    
                    <h4 className="font-semibold mb-1">
                      <button onClick={() => { if (event.lead_id) navigate(`/leads/${event.lead_id}`); }}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-left">
                        {event.lead_name}
                      </button>
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {event.description}
                    </p>
                    {event.remarks && (
                      <p className="text-xs text-slate-500 italic mb-3">"{event.remarks}"</p>
                    )}
                    
                    {/* Action buttons */}
                    {event.lead_id && (
                      <div className="flex gap-2 mb-3">
                        <Button
                          size="sm"
                          onClick={() => openUpdateDialog(event)}
                          className="text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Update
                        </Button>
                        {!event.meeting_completed && (
                          <Button
                            size="sm"
                            onClick={() => openActionDialog(event, "complete")}
                            className={`text-xs rounded-lg ${event.is_missed ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Done
                          </Button>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-1 text-xs text-slate-500">
                      {event.company && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3" />
                          <span>{event.company}</span>
                        </div>
                      )}
                      {event.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          <span>{event.phone}</span>
                        </div>
                      )}
                      {event.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{event.email}</span>
                        </div>
                      )}
                      {event.deal_value && (
                        <div className="flex items-center gap-2 text-emerald-600">
                          <IndianRupee className="w-3 h-3" />
                          <span>{event.deal_value.toLocaleString()}</span>
                        </div>
                      )}
                      {event.created_by_name && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200">
                          <User className="w-3 h-3" />
                          <span>By {event.created_by_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events List */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <CardHeader>
          <CardTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white">
            All Events ({events.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              No events found. Schedule meetings with your leads to see them here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Date & Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Lead</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Description</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">By</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...events].sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()).map((event) => (
                    <tr 
                      key={event.event_id}
                      className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                        event.is_missed ? "bg-red-50/50" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        {event.is_missed ? (
                          <Badge className="bg-red-500 text-white text-xs" data-testid={`table-missed-${event.event_id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Missed
                          </Badge>
                        ) : event.meeting_completed ? (
                          <Badge className="bg-emerald-500 text-white text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Done
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-200 text-slate-600 text-xs">
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className={`font-medium ${event.is_missed ? "text-red-700" : "text-slate-900 dark:text-white"}`}>
                          {format(parseISO(event.start), "MMM d, yyyy")}
                        </div>
                        <div className="text-xs text-slate-500">
                          {format(parseISO(event.start), "h:mm a")}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`${
                          event.is_missed ? "bg-red-100 text-red-700" :
                          event.type === "meeting" ? "bg-amber-100 text-amber-700" :
                          event.type === "call" ? "bg-green-100 text-green-700" :
                          "bg-teal-100 text-teal-700"
                        }`}>
                          {event.type.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => { if (event.lead_id) navigate(`/leads/${event.lead_id}`); }}
                          className={`font-medium hover:underline cursor-pointer text-left ${event.is_missed ? "text-red-700 hover:text-red-900" : "text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"}`}
                        >
                          {event.lead_name}
                        </button>
                        {event.company && <div className="text-xs text-slate-500">{event.company}</div>}
                      </td>
                      <td className="py-3 px-4 max-w-[200px] truncate text-slate-600 dark:text-slate-400">
                        {event.description}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {event.created_by_name}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {event.lead_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openUpdateDialog(event)}
                              className="h-7 px-2 text-indigo-600 hover:bg-indigo-50 text-xs font-medium"
                              title="Update Follow-up"
                            >
                              <Edit className="w-3.5 h-3.5 mr-1" />
                              Update
                            </Button>
                          )}
                          {!event.meeting_completed && event.lead_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openActionDialog(event, "complete")}
                              className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                              title="Mark Done"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {events.length > 20 && (
                <p className="text-center text-sm text-slate-500 py-4">
                  Showing 20 of {events.length} events
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Meeting Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              {actionType === "complete" ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Mark Meeting Complete
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                  Reschedule Meeting
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              {/* Event info */}
              <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                <button onClick={() => { if (selectedEvent.lead_id) { setShowEventDialog(false); navigate(`/leads/${selectedEvent.lead_id}`); } }}
                  className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-left">{selectedEvent.lead_name}</button>
                <p className="text-sm text-slate-500">
                  {selectedEvent.start && format(parseISO(selectedEvent.start), "PPP 'at' p")}
                </p>
                {selectedEvent.is_missed && (
                  <Badge className="mt-2 bg-red-100 text-red-700 text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    This meeting was missed
                  </Badge>
                )}
              </div>
              
              {/* Reschedule date picker */}
              {actionType === "reschedule" && (
                <div>
                  <Label htmlFor="reschedule-date" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    New Date & Time *
                  </Label>
                  <Input
                    id="reschedule-date"
                    type="datetime-local"
                    data-testid="reschedule-date-input"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="mt-1 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              )}
              
              {/* Remarks */}
              <div>
                <Label htmlFor="action-remarks" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {actionType === "complete" ? "Outcome / Notes" : "Reason for Rescheduling"}
                </Label>
                <Textarea
                  id="action-remarks"
                  data-testid="action-remarks-input"
                  placeholder={actionType === "complete" ? "How did the meeting go?" : "Why are you rescheduling?"}
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                  className="mt-1 bg-slate-50 border border-slate-200 rounded-xl"
                  rows={3}
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleMeetingAction}
                  data-testid="confirm-action-btn"
                  className={`flex-1 rounded-full py-3 font-semibold ${
                    actionType === "complete" 
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                      : "bg-amber-600 hover:bg-amber-700 text-white"
                  }`}
                >
                  {actionType === "complete" ? "Mark as Complete" : "Reschedule Meeting"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setActionDialogOpen(false)}
                  className="flex-1 rounded-full border-slate-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Update Follow-up Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <Edit className="w-5 h-5 text-indigo-600" />
              Update Follow-up
            </DialogTitle>
          </DialogHeader>

          {updateEvent && (
            <div className="space-y-4 mt-2">
              {/* Lead Info */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-indigo-600">{(updateEvent.lead_name || "?")[0].toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{updateEvent.lead_name}</p>
                  <p className="text-xs text-slate-500">{updateEvent.phone || updateEvent.email || "No contact"}</p>
                </div>
              </div>

              {/* Follow-up Status */}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Follow-up Status</Label>
                <Select value={updateForm.status} onValueChange={(v) => setUpdateForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400" /> Pending</div>
                    </SelectItem>
                    <SelectItem value="done">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Done</div>
                    </SelectItem>
                    <SelectItem value="not_interested">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" /> Not Interested</div>
                    </SelectItem>
                    <SelectItem value="converted">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500" /> Converted</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Next Follow-up Date (only if pending) */}
              {updateForm.status === "pending" && (
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Next Follow-up Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={updateForm.next_datetime}
                    onChange={(e) => setUpdateForm(f => ({ ...f, next_datetime: e.target.value }))}
                    className="h-11 rounded-xl"
                  />
                </div>
              )}

              {/* Remarks */}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Remarks</Label>
                <Textarea
                  placeholder="Enter follow-up notes..."
                  value={updateForm.remarks}
                  onChange={(e) => setUpdateForm(f => ({ ...f, remarks: e.target.value }))}
                  className="rounded-xl resize-none"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleUpdateFollowUp}
                  disabled={updateSaving}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11"
                >
                  {updateSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setUpdateDialogOpen(false)}
                  className="rounded-xl h-11"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendar;
